<template>
	<section v-if="show && inline" class="brief-writer-inline">
		<div class="brief-writer">
			<div class="bw-row" v-if="!contextReady">
				<p class="bw-help">We're missing some context. Close this panel and try again.</p>
			</div>

			<div class="bw-row" v-else-if="hasTimeline && resultChangeSummary">
				<div class="bw-feedback">
					<button type="button" class="bw-feedback-toggle" @click="toggleChangeSummary">
						<span class="bw-feedback-title">
							<span v-if="resultChangeSummaryIsNegative" class="bw-feedback-alert">[!]</span>
							<span>{{ isIterationCursor ? "Feedback on last edits" : "How the copy was generated" }}...</span>
						</span>
						<span class="bw-feedback-toggle-icon">{{ isChangeSummaryExpanded ? "[x]" : "[open]" }}</span>
					</button>

					<div class="bw-feedback-body" v-show="isChangeSummaryExpanded">
						<p class="bw-feedback-text">{{ resultChangeSummary }}</p>
					</div>
				</div>
			</div>

			<div class="bw-row no-margin-bottom">
				<div class="bw-field">
					<div class="bw-row" v-if="activeQuickEdits.length">
						<div class="bw-field">
							<label class="bw-label">{{ hasTimeline ? "Quick edits" : "Quick start" }}</label>
							<div class="bw-quick-edits bw-quick-edits--inline">
								<button
									v-for="edit in activeQuickEdits"
									:key="edit.id"
									type="button"
									class="button mini hollow bw-chip"
									:disabled="refineLoading || !contextReady"
									@click="applyQuickEdit(edit)">
									{{ edit.label }}
								</button>
							</div>
						</div>
					</div>

					<div class="bw-row no-margin-bottom" v-if="contextReady">
						<div class="bw-field">
							<div class="bw-refine-field">
								<FormInput
									type="textarea"
									v-model="refineNotes"
									:autoResize="true"
									theRef="bw-refine-textarea"
									:required="false"
									:noMarginBottom="true"
									:minHeight="100"
									classes="bw-editable-field bw-refine-textarea"
									backgroundColor="#679da20d"
									:label="hasTimeline ? 'Refine copy (optional)' : 'Pointers (optional)'"
									:placeholder="textareaPlaceholder">
								</FormInput>
							</div>

							<div class="bw-refine-actions" v-if="!hasTimeline || (hasTimeline && !isBaselineCursor)">
								<button
									v-if="!hasTimeline"
									class="button flip-colours no-case no-margin"
									type="button"
									:disabled="refineLoading || !!loadingMessage"
									@click="runAssist">
									Write
								</button>

								<button
									v-else
									class="button mini flip-colours no-case no-margin"
									type="button"
									:disabled="refineLoading || !runId || !hasRefineNotes"
									@click="applyRefinement()">
									{{ refineLoading ? "Editing…" : "Apply edits" }}
								</button>
							</div>

							<p class="bw-help bw-help--small" v-if="!runId && hasTimeline && !isBaselineCursor">
								Refinements aren't available at the moment.
							</p>

							<p
								class="bw-help bw-help--small bw-help--interactive has-icon"
								v-else
								role="button"
								tabindex="0"
								@click="openFeedbackWidget"
								@keydown.enter.prevent="openFeedbackWidget"
								@keydown.space.prevent="openFeedbackWidget">
								<svg data-v-18bd0bd9="" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#679da2" width="34" height="34">
									<path
										data-v-18bd0bd9=""
										d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"></path>
									<path data-v-18bd0bd9="" d="M7 9h10v2H7zm0-3h10v2H7z"></path>
								</svg>
								<span class="bw-help-text">
									Leave feedback for the Writing Tool here, we'd love to hear your thoughts! (even the negative!)
								</span>
							</p>
						</div>
					</div>
				</div>
			</div>

			<div class="bw-row no-margin-bottom" v-if="errorText">
				<p class="bw-error">{{ errorText }}</p>
			</div>
		</div>
	</section>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue";
import { useVueInstance } from "../composables/useVueInstance";

import FormInput from "./Form/Input.vue";
import ApiService from "../services/api.service";
import general from "../utils/general";

export default {
	name: "WritingTool",
	components: {
		FormInput,
	},
	props: {
		show: {
			type: Boolean,
			required: true,
		},
		context: {
			type: Object,
			required: false,
			default: null,
		},
		inline: {
			type: Boolean,
			required: false,
			default: false,
		},
		resumePayload: {
			type: Object,
			required: false,
			default: null,
		},
	},
	emits: ["update", "loading-message", "ui-state", "cache-state"],
	setup(props, { emit }) {
		const { $toast, store } = useVueInstance();

		const MAX_UNDO_STEPS = 5;

		const loadingMessage = ref("");
		const refineLoading = ref(false);
		const errorText = ref("");

		const runId = ref(null);

		const normaliseQuickEditsPayload = (value) => {
			if (Array.isArray(value)) {
				return { new: [], edit: value };
			}
			const v = value && typeof value === "object" ? value : {};
			return {
				new: Array.isArray(v.new) ? v.new : [],
				edit: Array.isArray(v.edit) ? v.edit : [],
			};
		};

		const quickEdits = ref({ new: [], edit: [] });
		const quickEditsType = ref(null);

		const activeType = ref("request");

		// Timeline state (cursor model, redo truncation on edit)
		const timeline = ref([]);
		const timelineCursor = ref(-1);
		const isRestoringTimeline = ref(false);
		const resumePayloadConsumed = ref(false);

		// Derived display state
		const resultText = ref("");
		const resultBrief = ref(null);
		const resultChangeSummary = ref("");
		const resultChangeSummaryIsNegative = ref(false);
		const isChangeSummaryExpanded = ref(false);
		const briefWriterCostInfo = ref(null);

		// Editable fields
		const refineNotes = ref("");
		const editedRequestText = ref("");
		const editedFeatureTitle = ref("");
		const editedFeatureContent = ref("");
		const editedLookingFor = ref("");
		const editedNotLookingFor = ref("");

		const contextReady = computed(() => !!props.context && typeof props.context === "object");
		const contextType = computed(() => {
			const ctx = props.context && typeof props.context === "object" ? props.context : {};
			return ctx.mode === "feature" ? "feature" : "request";
		});

		const textareaPlaceholder = computed(() => {
			if (hasTimeline.value) {
				return resultChangeSummary.value ? "Tell the Writing Tool what to change (reading the feedback above may help)" : "Tell the Writing Tool what to change";
			}
			let m = "Add a few key points relevant to the "+(contextType.value !== 'request' ? 'opportunity' : 'request')+" (it's already aware of your brands profile) or leave it blank and the tool will generate a first draft for you.";
			if (props.context && props.context.mode === "request") {
				const collaborationTypeIds = Array.isArray(props.context.collaborationTypeIds) ? props.context.collaborationTypeIds : [];
				if (collaborationTypeIds.length) {
					if ( props.context.messageDraft ) {
						m += ` 
Your message and selected partnership types will also be used as a reference.`;
					}
					else {
						m += ` 
Your selected partnership types will also be used as a reference.`;
					}

				}
				else if ( props.context.messageDraft ) {
					m += ` 
Your message will also be used as a reference.`;
				}
			}
			return m;
		});

		const openFeedbackWidget = (payload = {}) => {
			const nextPayload = payload && typeof payload === "object" && !("target" in payload) ? payload : {};

			store.dispatch("feedback/openWidget", {
				type: "feedback",
				...nextPayload,
			});
		};

		const hasTimeline = computed(() => Array.isArray(timeline.value) && timeline.value.length > 0);

		const isBaselineCursor = computed(() => {
			if (!hasTimeline.value) return false;
			if (timelineCursor.value < 0) return false;
			const entry = timeline.value[timelineCursor.value];
			return !!entry && entry.kind === "baseline";
		});

		const isIterationCursor = computed(() => {
			if (!hasTimeline.value) return false;
			if (timelineCursor.value < 0) return false;
			const entry = timeline.value[timelineCursor.value];
			return !!entry && entry.kind === "iteration";
		});

		const activeQuickEdits = computed(() => {
			const qe = quickEdits.value;
			if (Array.isArray(qe)) return qe; // legacy defensive
			const list = !hasTimeline.value || isBaselineCursor.value ? qe && qe.new : qe && qe.edit;
			return Array.isArray(list) ? list : [];
		});

		const hasRefineNotes = computed(() => typeof refineNotes.value === "string" && refineNotes.value.trim().length > 0);

		const historyTotal = computed(() => timeline.value.length);
		const minUndoCursor = computed(() => Math.max(0, historyTotal.value - 1 - MAX_UNDO_STEPS));
		const canUndo = computed(() => historyTotal.value > 1 && timelineCursor.value > minUndoCursor.value);
		const canRedo = computed(() => historyTotal.value > 1 && timelineCursor.value < historyTotal.value - 1);

		const emitUiState = () => {
			emit("ui-state", {
				hasTimeline: hasTimeline.value,
				isBaselineCursor: isBaselineCursor.value,
				runId: runId.value,
				canUndo: canUndo.value,
				canRedo: canRedo.value,
			});
		};

		const normaliseCursorDescriptor = (value) => {
			if (!value || typeof value !== "object") return null;

			const kind = value.kind === "iteration" ? "iteration" : value.kind === "base" ? "base" : value.kind === "baseline" ? "baseline" : null;

			if (!kind) return null;

			if (kind === "baseline") {
				return { kind: "baseline", iterationIndex: null };
			}

			if (kind === "base") {
				return { kind: "base", iterationIndex: 0 };
			}

			const idxRaw = value.iterationIndex;
			const idx = idxRaw === 0 || idxRaw === "0" ? 0 : idxRaw != null ? parseInt(idxRaw, 10) : null;

			if (idx === null || isNaN(idx)) return null;
			return { kind: "iteration", iterationIndex: idx };
		};

		const cursorDescriptorFromEntry = (entry) => {
			if (!entry || typeof entry !== "object") {
				return { kind: "baseline", iterationIndex: null };
			}

			if (entry.kind === "baseline") {
				return { kind: "baseline", iterationIndex: null };
			}

			if (entry.kind === "base") {
				return { kind: "base", iterationIndex: 0 };
			}

			if (entry.kind === "iteration") {
				const idx = typeof entry.iterationIndex === "number" ? entry.iterationIndex : null;
				return { kind: "iteration", iterationIndex: idx };
			}

			return { kind: "baseline", iterationIndex: null };
		};

		const findCursorIndexInTimeline = (timelineEntries, cursor) => {
			const c = normaliseCursorDescriptor(cursor);
			if (!c) return null;
			if (!Array.isArray(timelineEntries) || !timelineEntries.length) return null;

			for (let i = 0; i < timelineEntries.length; i++) {
				const entry = timelineEntries[i];
				if (!entry) continue;

				if (c.kind === "baseline" && entry.kind === "baseline") return i;

				// Treat base and iteration(0) as equivalent for resume robustness.
				if (c.kind === "base") {
					if (entry.kind === "base") return i;
					if (entry.kind === "iteration") {
						const entryIdx = typeof entry.iterationIndex === "number" ? entry.iterationIndex : null;
						if (entryIdx === 0) return i;
					}
					continue;
				}

				if (c.kind === "iteration") {
					if (entry.kind === "iteration") {
						const entryIdx = typeof entry.iterationIndex === "number" ? entry.iterationIndex : null;
						if (entryIdx === c.iterationIndex) return i;
					}

					if (c.iterationIndex === 0 && entry.kind === "base") return i;
				}
			}

			return null;
		};

		const emitCacheState = () => {
			const cursorEntry = Array.isArray(timeline.value) && timelineCursor.value >= 0 ? timeline.value[timelineCursor.value] : null;

			const cursor = cursorDescriptorFromEntry(cursorEntry);

			emit("cache-state", {
				runId: runId.value,
				type: activeType.value,
				cursor,
				refineNotesDraft: typeof refineNotes.value === "string" ? refineNotes.value : "",
			});
		};

		const resetState = () => {
			loadingMessage.value = "";
			refineLoading.value = false;
			errorText.value = "";

			runId.value = null;

			activeType.value = "request";

			timeline.value = [];
			timelineCursor.value = -1;
			isRestoringTimeline.value = false;

			resultText.value = "";
			resultBrief.value = null;
			resultChangeSummary.value = "";
			resultChangeSummaryIsNegative.value = false;
			isChangeSummaryExpanded.value = false;
			briefWriterCostInfo.value = null;

			refineNotes.value = "";
			editedRequestText.value = "";
			editedFeatureTitle.value = "";
			editedFeatureContent.value = "";
			editedLookingFor.value = "";
			editedNotLookingFor.value = "";
		};

		const parseListFromText = (text) => {
			if (typeof text !== "string") return [];
			return text
				.split("\n")
				.map((line) => line.trim())
				.filter((line) => line.length);
		};

		const buildApplyPayload = () => {
			if (activeType.value === "request") {
				return {
					mode: "request",
					message: (editedRequestText.value || "").trim(),
				};
			}

			return {
				mode: "feature",
				title: (editedFeatureTitle.value || "").trim(),
				content: (editedFeatureContent.value || "").trim(),
				wants: parseListFromText(editedLookingFor.value),
				bads: parseListFromText(editedNotLookingFor.value),
			};
		};

		const emitInlineUpdate = (options = {}) => {
			if (!props.inline) return;
			if (!hasTimeline.value) return;
			if (!options.force && isRestoringTimeline.value) return;
			const payload = buildApplyPayload();
			if (options && typeof options._source === "string" && options._source.trim().length) {
				payload._source = options._source.trim();
			}
			emit("update", payload);
		};

		const buildBaselineEntry = (payload) => {
			if (!payload) return null;

			if (payload.type === "request") {
				return {
					kind: "baseline",
					iterationIndex: null,
					refineNotesText: "",
					message: typeof payload.messageDraft === "string" ? payload.messageDraft : "",
					brief: null,
					changeSummary: "",
					changeSummaryIsNegative: false,
				};
			}

			return {
				kind: "baseline",
				iterationIndex: null,
				refineNotesText: "",
				message: null,
				brief: {
					title: typeof payload.titleDraft === "string" ? payload.titleDraft : "",
					content: typeof payload.contentDraft === "string" ? payload.contentDraft : "",
					what_we_re_looking_for: Array.isArray(payload.wants) ? payload.wants : [],
					what_we_re_not_looking_for: Array.isArray(payload.bads) ? payload.bads : [],
				},
				changeSummary: "",
				changeSummaryIsNegative: false,
			};
		};

		const applyResumePayload = (data, baselineEntry) => {
			if (!data || typeof data !== "object") return false;

			const resumeTimeline = Array.isArray(data.timeline) ? data.timeline : null;
			if (!resumeTimeline || !resumeTimeline.length) return false;

			const newRunId = data.runId != null ? parseInt(data.runId, 10) : null;
			runId.value = Number.isFinite(newRunId) ? newRunId : null;

			if (typeof data.type === "string") {
				activeType.value = data.type === "feature" ? "feature" : "request";
			}

			quickEdits.value = normaliseQuickEditsPayload(data.quickEdits);
			quickEditsType.value = activeType.value;
			briefWriterCostInfo.value = data.briefWriterCostInfo && typeof data.briefWriterCostInfo === "object" ? data.briefWriterCostInfo : null;

			timeline.value = baselineEntry ? [baselineEntry, ...resumeTimeline] : resumeTimeline;

			let nextCursor = timeline.value.length - 1;
			const foundIndex = findCursorIndexInTimeline(timeline.value, data.cursor);

			if (typeof foundIndex === "number") {
				nextCursor = foundIndex;
			}

			applyTimelineCursor(nextCursor, { _source: "resume" });

			// Restore any in-progress refine copy draft (client-only state).
			if (typeof data.refineNotesDraft === "string") {
				refineNotes.value = data.refineNotesDraft;

				if (timeline.value && timelineCursor.value >= 0 && timeline.value[timelineCursor.value]) {
					timeline.value[timelineCursor.value].refineNotesText = refineNotes.value;
				}

				emitCacheState();
			}

			// Optional: override edited draft fields so inline parent matches cached BrandConnect form state.
			const draft = data.draft && typeof data.draft === "object" ? data.draft : null;
			if (draft) {
				if (activeType.value === "request") {
					if (typeof draft.requestText === "string") {
						editedRequestText.value = draft.requestText;
					}
				} else {
					if (typeof draft.featureTitle === "string") {
						editedFeatureTitle.value = draft.featureTitle;
					}
					if (typeof draft.featureContent === "string") {
						editedFeatureContent.value = draft.featureContent;
					}
					if (typeof draft.lookingForText === "string") {
						editedLookingFor.value = draft.lookingForText;
					}
					if (typeof draft.notLookingForText === "string") {
						editedNotLookingFor.value = draft.notLookingForText;
					}
				}

				emitInlineUpdate({ force: true, _source: "resume-draft" });
			}

			if (!runId.value) {
				const msg = "Draft created, but runId was not returned by the server (refine disabled).";
				if ($toast && $toast.warning) $toast.warning(msg);
			}

			return true;
		};

		const syncEditableFields = () => {
			if (activeType.value === "request") {
				editedRequestText.value = resultText.value || "";
				return;
			}

			const brief = resultBrief.value;
			if (!brief || typeof brief !== "object") {
				editedFeatureTitle.value = "";
				editedFeatureContent.value = "";
				editedLookingFor.value = "";
				editedNotLookingFor.value = "";
				return;
			}

			editedFeatureTitle.value = typeof brief.title === "string" ? brief.title : "";
			editedFeatureContent.value = typeof brief.content === "string" ? brief.content : "";

			editedLookingFor.value = Array.isArray(brief.what_we_re_looking_for) ? brief.what_we_re_looking_for.join("\n") : "";
			editedNotLookingFor.value = Array.isArray(brief.what_we_re_not_looking_for) ? brief.what_we_re_not_looking_for.join("\n") : "";
		};

		const toggleChangeSummary = () => {
			if (!resultChangeSummary.value) return;
			isChangeSummaryExpanded.value = !isChangeSummaryExpanded.value;
		};

		const applyTimelineCursor = (nextCursor, options = {}) => {
			if (!Array.isArray(timeline.value) || !timeline.value.length) return;

			const safeCursor = Math.min(Math.max(nextCursor, 0), timeline.value.length - 1);
			const entry = timeline.value[safeCursor];

			isRestoringTimeline.value = true;
			timelineCursor.value = safeCursor;

			if (activeType.value === "request") {
				resultText.value = entry && typeof entry.message === "string" ? entry.message : "";
				resultBrief.value = null;
			} else {
				resultBrief.value = entry && entry.brief ? entry.brief : null;
				resultText.value = "";
			}

			refineNotes.value = entry && typeof entry.refineNotesText === "string" ? entry.refineNotesText : "";
			resultChangeSummary.value = entry && typeof entry.changeSummary === "string" ? entry.changeSummary : "";

			resultChangeSummaryIsNegative.value =
				!!entry && typeof entry.changeSummaryIsNegative === "boolean" ? entry.changeSummaryIsNegative : false;

			isChangeSummaryExpanded.value = !!resultChangeSummary.value && resultChangeSummaryIsNegative.value === true;

			syncEditableFields();

			emitInlineUpdate({ force: true, ...(options || {}) });
			emitUiState();
			emitCacheState();

			nextTick(() => {
				isRestoringTimeline.value = false;
			});
		};

		const truncateRedoHistoryIfNeeded = () => {
			if (isRestoringTimeline.value) return;
			if (!Array.isArray(timeline.value) || !timeline.value.length) return;
			if (timelineCursor.value < 0) return;

			// Persist current notes into the active entry.
			if (timeline.value[timelineCursor.value]) {
				timeline.value[timelineCursor.value].refineNotesText = refineNotes.value;
			}

			// If the user edits after undo, drop redo history.
			if (timelineCursor.value < timeline.value.length - 1) {
				timeline.value = timeline.value.slice(0, timelineCursor.value + 1);
			}
		};

		const undo = () => {
			if (!canUndo.value) return;
			applyTimelineCursor(Math.max(minUndoCursor.value, timelineCursor.value - 1));
		};

		const redo = () => {
			if (!canRedo.value) return;
			applyTimelineCursor(timelineCursor.value + 1);
		};

		const buildRunPayload = () => {
			const ctx = props.context || {};
			const mode = ctx.mode === "feature" ? "feature" : "request";

			if (mode === "request") {
				return {
					type: "request",
					recipientBrandId: ctx.recipientBrandId,
					collaborationTypeIds: Array.isArray(ctx.collaborationTypeIds) ? ctx.collaborationTypeIds : [],
					collaborationTypeLabels: Array.isArray(ctx.collaborationTypeLabels) ? ctx.collaborationTypeLabels : [],
					messageDraft: typeof ctx.messageDraft === "string" ? ctx.messageDraft : "",
					refineNotesDraft: typeof refineNotes.value === "string" ? refineNotes.value : "",
				};
			}

			return {
				type: "feature",
				titleDraft: typeof ctx.titleDraft === "string" ? ctx.titleDraft : "",
				contentDraft: typeof ctx.contentDraft === "string" ? ctx.contentDraft : "",
				wants: Array.isArray(ctx.wants) ? ctx.wants : [],
				bads: Array.isArray(ctx.bads) ? ctx.bads : [],
				refineNotesDraft: typeof refineNotes.value === "string" ? refineNotes.value : "",
			};
		};

		const normaliseRunLaunchMeta = (overrideInstruction = null) => {
			if (
				overrideInstruction &&
				typeof overrideInstruction === "object" &&
				typeof overrideInstruction.instruction === "string" &&
				overrideInstruction.instruction.trim().length
			) {
				return {
					instruction: overrideInstruction.instruction.trim(),
					launchSource: "quick_start",
					launchSourceLabel: typeof overrideInstruction.label === "string" ? overrideInstruction.label : "",
					launchSourceId: typeof overrideInstruction.id === "string" ? overrideInstruction.id : "",
				};
			}

			if (typeof overrideInstruction === "string" && overrideInstruction.trim().length) {
				return {
					instruction: overrideInstruction.trim(),
					launchSource: "quick_start",
					launchSourceLabel: "",
					launchSourceId: "",
				};
			}

			return {
				instruction: "",
				launchSource: "manual",
				launchSourceLabel: "",
				launchSourceId: "",
			};
		};

		const logWritingToolCostInfo = (endpoint, responseData) => {
			if (!responseData || typeof responseData !== "object") {
				briefWriterCostInfo.value = null;
				return;
			}

			briefWriterCostInfo.value =
				responseData.briefWriterCostInfo && typeof responseData.briefWriterCostInfo === "object" ? responseData.briefWriterCostInfo : null;

			if (!responseData.briefWriterCostInfo) return;
		};

		const captureWritingToolUse = () => {
			general.posthogCapture("writing-tool-use");
		};

		const runAssist = async (overrideInstruction = null) => {
			if (!contextReady.value) return;
			if (loadingMessage.value) return;

			errorText.value = "";

			const payload = buildRunPayload();
			const launchMeta = normaliseRunLaunchMeta(overrideInstruction);
			if (launchMeta.instruction) {
				payload.refineNotesDraft = launchMeta.instruction;
			}
			payload.launchSource = launchMeta.launchSource;
			if (launchMeta.launchSourceLabel) {
				payload.launchSourceLabel = launchMeta.launchSourceLabel;
			}
			if (launchMeta.launchSourceId) {
				payload.launchSourceId = launchMeta.launchSourceId;
			}

			activeType.value = payload.type === "feature" ? "feature" : "request";
			const baselineEntry = props.inline ? buildBaselineEntry(payload) : null;

			if (!resumePayloadConsumed.value && props.resumePayload && props.resumePayload.resume === true) {
				if (applyResumePayload(props.resumePayload, baselineEntry)) {
					resumePayloadConsumed.value = true;
					return;
				}
			}

			loadingMessage.value = "Generating a draft... this usually takes a few seconds.";
			captureWritingToolUse();

			try {
				const response = await ApiService.post("matching/brief-writer/assist/run", payload);
				const data = response && response.data ? response.data : {};
				logWritingToolCostInfo("assist/run", data);

				if (applyResumePayload(data, baselineEntry)) {
					return;
				}

				const newRunId = data.runId != null ? parseInt(data.runId, 10) : null;
				runId.value = Number.isFinite(newRunId) ? newRunId : null;

				quickEdits.value = normaliseQuickEditsPayload(data.quickEdits);
				quickEditsType.value = activeType.value;

				const result = data.data || {};
				const changeSummaryIsNegative = result.changeSummaryIsNegative === true;

				const baseEntry = {
					kind: "base",
					iterationIndex: 0,
					refineNotesText: changeSummaryIsNegative && typeof payload.refineNotesDraft === "string" ? payload.refineNotesDraft : "",
					message: activeType.value === "request" ? result.message || "" : null,
					brief: activeType.value === "feature" ? result.brief || null : null,
					changeSummary: typeof result.changeSummary === "string" ? result.changeSummary : "",
					changeSummaryIsNegative,
				};

				timeline.value = baselineEntry ? [baselineEntry, baseEntry] : [baseEntry];
				timelineCursor.value = baselineEntry ? 1 : 0;

				applyTimelineCursor(timelineCursor.value);
				emitInlineUpdate({ force: true });

				if (!runId.value) {
					// Not fatal for viewing/using the draft, but refinement won't work.
					const msg = "Draft created, but runId was not returned by the server (refine disabled).";
					if ($toast && $toast.warning) $toast.warning(msg);
				}
			} catch (err) {
				console.error("WritingTool.runAssist error:", err);
				errorText.value = "There was a problem generating a draft. Please try again.";
				//if ($toast && $toast.error) $toast.error(errorText.value);
			} finally {
				loadingMessage.value = "";
			}
		};

		const applyRefinement = async (overrideInstruction = null, timelineRefineNotesOverride = null, isQuickEdit = false, quickEditMeta = null) => {
			if (refineLoading.value) return;
			if (!runId.value) return;
			if (!hasTimeline.value) return;

			refineLoading.value = true;
			loadingMessage.value = "Editing...";
			errorText.value = "";
			captureWritingToolUse();

			try {
				// If the user refined after undo, ensure redo is cleared before branching.
				if (timelineCursor.value >= 0 && timelineCursor.value < timeline.value.length - 1) {
					timeline.value = timeline.value.slice(0, timelineCursor.value + 1);
				}

				const cursorEntry = timeline.value[timelineCursor.value] || null;
				const baseIterationIndex = cursorEntry && typeof cursorEntry.iterationIndex === "number" ? cursorEntry.iterationIndex : 0;

				// Use overrideInstruction if provided (from quick edits), otherwise use refineNotes
				const instructionText =
					overrideInstruction !== null ? overrideInstruction : typeof refineNotes.value === "string" ? refineNotes.value : "";

				const timelineRefineNotesText = timelineRefineNotesOverride !== null ? timelineRefineNotesOverride : instructionText;

				const delta = {
					refine_notes_text: instructionText,
				};

				if (isQuickEdit) {
					delta.quick_edit = true;
					if (quickEditMeta && typeof quickEditMeta === "object" && typeof quickEditMeta.id === "string" && quickEditMeta.id.trim()) {
						delta.quick_edit_source_id = quickEditMeta.id.trim();
					}
					if (quickEditMeta && typeof quickEditMeta === "object" && typeof quickEditMeta.label === "string" && quickEditMeta.label.trim()) {
						delta.quick_edit_source_label = quickEditMeta.label.trim();
					}
				}

				if (activeType.value === "request") {
					const edited = (editedRequestText.value || "").trim();
					const current = (resultText.value || "").trim();

					if (edited && edited !== current) {
						delta.override_message = edited;
					}
				} else {
					delta.override_brief = {
						title: (editedFeatureTitle.value || "").trim(),
						content: (editedFeatureContent.value || "").trim(),
						what_we_re_looking_for: parseListFromText(editedLookingFor.value),
						what_we_re_not_looking_for: parseListFromText(editedNotLookingFor.value),
					};
				}

				const payload = {
					runId: runId.value,
					baseIterationIndex,
					delta,
				};

				const response = await ApiService.post("matching/brief-writer/assist/refine", payload);
				const data = response && response.data ? response.data : {};
				logWritingToolCostInfo("assist/refine", data);
				const result = data.data || {};

				quickEdits.value = normaliseQuickEditsPayload(data.quickEdits);
				quickEditsType.value = activeType.value;

				const changeSummaryIsNegative = result.changeSummaryIsNegative === true;

				const nextIterationIndex = data.iteration && typeof data.iteration.iterationIndex === "number" ? data.iteration.iterationIndex : null;

				const nextIterationId = data.iteration && data.iteration.id != null ? parseInt(data.iteration.id, 10) : null;

				const newEntry = {
					kind: "iteration",
					id: nextIterationId,
					iterationIndex: nextIterationIndex,
					refineNotesText: changeSummaryIsNegative ? timelineRefineNotesText : "",
					message: activeType.value === "request" ? result.message || "" : null,
					brief: activeType.value === "feature" ? result.brief || null : null,
					changeSummary: typeof result.changeSummary === "string" ? result.changeSummary : "",
					changeSummaryIsNegative,
				};

				timeline.value = [...timeline.value, newEntry];
				timelineCursor.value = timeline.value.length - 1;
				applyTimelineCursor(timelineCursor.value);
				emitInlineUpdate({ force: true });
			} catch (err) {
				console.error("WritingTool.applyRefinement error:", err);
				errorText.value = "There was a problem refining this draft. Please try again.";
				if ($toast && $toast.error) $toast.error(errorText.value);
			} finally {
				refineLoading.value = false;
				loadingMessage.value = "";
			}
		};

		const applyQuickEdit = async (edit) => {
			if (!edit || typeof edit.instruction !== "string" || !edit.instruction.trim()) return;

			const nextInstruction = edit.instruction.trim();
			const currentRefineNotes = typeof refineNotes.value === "string" ? refineNotes.value : "";

			if (!runId.value || !hasTimeline.value) {
				await runAssist(edit);
				return;
			}

			// Apply the quick edit instruction directly without updating the refineNotes textarea
			await applyRefinement(nextInstruction, currentRefineNotes, true, edit);
		};

		const emitEmptyInlineUpdate = () => {
			if (!props.inline) return;

			const mode = props.context && props.context.mode === "feature" ? "feature" : "request";

			if (mode === "feature") {
				emit("update", { mode: "feature", title: "", content: "", wants: [], bads: [], flush: true });
				return;
			}

			emit("update", { mode: "request", message: "", flush: true });
		};

		const startAgain = async () => {
			if (!runId.value) return;
			if (refineLoading.value) return;

			errorText.value = "";

			try {
				await ApiService.post("matching/brief-writer/assist/reset", { runId: runId.value });

				resetState();
				await nextTick();
				emitEmptyInlineUpdate();
			} catch (err) {
				console.error("WritingTool.startAgain error:", err);
				errorText.value = "There was a problem starting again. Please try again.";
				if ($toast && $toast.error) $toast.error(errorText.value);
			}
		};

		const resetAndRun = async () => {
			// "Start again" semantics, but keep the panel open and immediately generate a fresh draft.
			if (refineLoading.value) return;
			if (!contextReady.value) return;

			errorText.value = "";

			try {
				if (runId.value) {
					await ApiService.post("matching/brief-writer/assist/reset", { runId: runId.value });
				}

				// Reset local UI state and rerun the assistant.
				resetState();
				resumePayloadConsumed.value = false;
				await nextTick();
				await runAssist();
			} catch (err) {
				console.error("WritingTool.resetAndRun error:", err);
				errorText.value = "There was a problem starting again. Please try again.";
				if ($toast && $toast.error) $toast.error(errorText.value);
			}
		};

		const clearTimeline = async () => {
			resetState();
			await nextTick();
			emitEmptyInlineUpdate();
		};

		const loadQuickEdits = async () => {
			if (!contextReady.value) return;
			const type = contextType.value;
			if (quickEditsType.value === type && activeQuickEdits.value.length) return;

			try {
				const response = await ApiService.get("matching/brief-writer/assist/quick-edits", {
					params: { type },
				});
				const data = response && response.data ? response.data : {};
				if (data.quickEdits) {
					quickEdits.value = normaliseQuickEditsPayload(data.quickEdits);
					quickEditsType.value = type;
				}
			} catch (err) {
				console.error("WritingTool.loadQuickEdits error:", err);
			}
		};

		// Redo truncation on any edit (same semantics as the demo flow)
		watch(refineNotes, truncateRedoHistoryIfNeeded);
		watch(editedRequestText, truncateRedoHistoryIfNeeded);
		watch(editedFeatureTitle, truncateRedoHistoryIfNeeded);
		watch(editedFeatureContent, truncateRedoHistoryIfNeeded);
		watch(editedLookingFor, truncateRedoHistoryIfNeeded);
		watch(editedNotLookingFor, truncateRedoHistoryIfNeeded);

		watch(refineNotes, () => {
			if (isRestoringTimeline.value) return;
			emitCacheState();
		});

		watch(
			() => [contextReady.value, contextType.value],
			() => {
				loadQuickEdits();
			},
			{ immediate: true }
		);

		const tryApplyResumePayload = async () => {
			if (resumePayloadConsumed.value) return;
			if (!props.resumePayload || props.resumePayload.resume !== true) return;
			if (!contextReady.value) return;

			resetState();
			await nextTick();

			const payload = buildRunPayload();
			activeType.value = payload.type === "feature" ? "feature" : "request";
			const baselineEntry = props.inline ? buildBaselineEntry(payload) : null;

			if (applyResumePayload(props.resumePayload, baselineEntry)) {
				resumePayloadConsumed.value = true;
			}
		};

		watch(runId, () => {
			emitCacheState();
		});

		const emitInlineUpdateOnEdit = () => {
			emitInlineUpdate();
		};

		watch(editedRequestText, emitInlineUpdateOnEdit);
		watch(editedFeatureTitle, emitInlineUpdateOnEdit);
		watch(editedFeatureContent, emitInlineUpdateOnEdit);
		watch(editedLookingFor, emitInlineUpdateOnEdit);
		watch(editedNotLookingFor, emitInlineUpdateOnEdit);

		// Apply resume payload without auto-running a new draft.
		watch(
			() => props.resumePayload,
			(next, prev) => {
				if (next !== prev) {
					resumePayloadConsumed.value = false;
				}
				tryApplyResumePayload();
			},
			{ immediate: true }
		);

		watch(
			contextReady,
			(next) => {
				if (next) tryApplyResumePayload();
			},
			{ immediate: true }
		);

		watch(
			() => loadingMessage.value,
			(next, prev) => {
				emit("loading-message", next);
			}
		);

		watch(
			() => [timelineCursor.value, Array.isArray(timeline.value) ? timeline.value.length : 0],
			() => {
				emitUiState();
			},
			{ immediate: true }
		);

		// Watch for show prop changes to track metadata for feedback widget
		watch(
			() => props.show,
			(newVal) => {
				if (newVal) {
					// Set metadata when brief writer opens
					if (runId.value) {
						store.commit("feedback/setMetadata", {
							key: "aiBriefWriterId",
							value: runId.value,
						});
					}
					store.commit("feedback/setOpenWindow", "WritingTool");
				} else {
					// Clear metadata when brief writer closes
					store.commit("feedback/clearMetadata", "aiBriefWriterId");
					store.commit("feedback/clearMetadata", "aiIterationId");
					store.commit("feedback/removeOpenWindow", "WritingTool");
				}
			}
		);

		// Watch runId changes to update metadata
		watch(runId, (newVal) => {
			if (newVal && props.show) {
				store.commit("feedback/setMetadata", {
					key: "aiBriefWriterId",
					value: newVal,
				});
			}
		});

		// Watch timeline cursor for iteration tracking
		watch(timelineCursor, (newVal) => {
			if (props.show && hasTimeline.value && newVal >= 0) {
				const entry = timeline.value[newVal];
				if (entry && entry.iterationIndex != null) {
					store.commit("feedback/setMetadata", {
						key: "aiIterationId",
						value: entry.iterationIndex,
					});
				}
			}
		});

		return {
			refineLoading,
			loadingMessage,
			errorText,

			runId,
			quickEdits,
			activeQuickEdits,
			hasRefineNotes,
			activeType,

			contextReady,

			timeline,
			timelineCursor,
			hasTimeline,
			isBaselineCursor,
			isIterationCursor,

			refineNotes,
			editedRequestText,
			editedFeatureTitle,
			editedFeatureContent,
			editedLookingFor,
			editedNotLookingFor,

			resultChangeSummary,
			resultChangeSummaryIsNegative,
			isChangeSummaryExpanded,
			toggleChangeSummary,
			openFeedbackWidget,
			briefWriterCostInfo,

			canUndo,
			canRedo,
			undo,
			redo,

			runAssist,
			applyRefinement,
			applyQuickEdit,
			startAgain,
			resetAndRun,
			clearTimeline,

			textareaPlaceholder,
			contextType
		};
	},
};
</script>

<style scoped lang="less">
@import "./../assets/css/less/_settings.less";

hr {
	border-color: var(--bw-accent-border, fade(@accentAssistTeal, 18%));
}

.brief-writer-inline {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0;

	.bw-quick-edits--inline {
		gap: 6px;
	}

	.bw-chip {
		border-radius: @radiusPill;
		padding: 4px 10px;
		font-size: 12px;
		border-color: var(--bw-accent-border, fade(@accentAssistTeal, 30%));
		color: var(--bw-accent, @accentAssistTeal);
		background-color: #ffffff;
	}

	.bw-icon-button {
		position: relative;
		width: 34px;
		height: 34px;
		border-radius: @radiusPill;
		border: 1px solid var(--bw-accent-border, fade(@accentAssistTeal, 18%));
		background-color: #ffffff;
		color: var(--bw-accent, @accentAssistTeal);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		cursor: pointer;
		transition:
			background-color 0.2s ease,
			border-color 0.2s ease;
		outline: none;
	}

	.bw-icon-button:hover {
		background-color: var(--bw-accent-soft, fade(@accentAssistTeal, 6%));
		border-color: var(--bw-accent-border-strong, fade(@accentAssistTeal, 30%));
	}

	.bw-icon {
		width: 16px;
		height: 16px;
	}

	.bw-icon-tooltip {
		position: absolute;
		left: 50%;
		top: calc(100% + 10px);
		z-index: 10;
		width: max-content;
		max-width: 240px;
		padding: 8px 10px;
		border-radius: @radiusLg;
		border: 1px solid var(--bw-accent-tooltip-border, fade(@accentAssistTeal, 18%));
		background: @surfacePanel;
		color: var(--bw-accent, @accentAssistTeal);
		font-size: 12px;
		font-weight: 600;
		text-transform: none;
		letter-spacing: normal;
		line-height: 1.2;
		.elevation.floatingPanel();

		opacity: 0;
		visibility: hidden;
		transform: translate(-50%, -4px);
		transition:
			opacity 0.12s ease,
			transform 0.12s ease,
			visibility 0s linear 0.12s;

		&::before {
			content: "";
			position: absolute;
			top: -6px;
			left: 50%;
			width: 10px;
			height: 10px;
			background: @surfacePanel;
			border-left: 1px solid var(--bw-accent-tooltip-border, fade(@accentAssistTeal, 18%));
			border-top: 1px solid var(--bw-accent-tooltip-border, fade(@accentAssistTeal, 18%));
			transform: translateX(-50%) rotate(45deg);
		}
	}

	.bw-icon-button:hover .bw-icon-tooltip,
	.bw-icon-button:focus .bw-icon-tooltip,
	.bw-icon-button:focus-within .bw-icon-tooltip {
		opacity: 1;
		visibility: visible;
		transform: translate(-50%, 0);
		transition:
			opacity 0.12s ease,
			transform 0.12s ease,
			visibility 0s;
	}

	.bw-refine-field {
		position: relative;
	}

	.bw-refine-textarea {
		padding-bottom: 0;
		padding-right: 0;
	}

	.bw-primary-action {
		display: flex;
		justify-content: flex-end;
		margin: 8px 0 10px;
	}

	.bw-refine-actions {
		display: flex;
		justify-content: flex-end;
		margin-top: 15px;

		.button {
			margin: 0;
		}
	}
}

.brief-writer {
	.bw-row {
		margin-bottom: 16px;

		&--two {
			display: flex;
			flex-wrap: wrap;
			gap: 16px;
			align-items: flex-start;

			.bw-field {
				flex: 1 1 0;
				min-width: 0;
			}
		}
		&.no-margin-bottom {
			margin-bottom: 0;
		}
	}

	.bw-field {
		display: flex;
		flex-direction: column;
	}

	.bw-label {
		font-weight: @bold;
		margin-bottom: 6px;
		color: @darkContrast;
		font-size: 16px;

		&--section {
			font-size: 15px;
			margin-bottom: 12px;
			border-bottom: 1px solid @borderRoseSubtle;
			padding-bottom: 6px;
		}
	}

	.input {
		width: 100%;
		padding: 8px 10px;
		border-radius: @radiusXs;
		border: 1px solid @borderRoseSubtle;
		font-size: 14px;
		box-sizing: border-box;
	}

	.textarea {
		width: 100%;
		padding: 8px 10px;
		border-radius: @radiusXs;
		border: 1px solid @borderRoseSubtle;
		font-size: 14px;
		resize: vertical;
		box-sizing: border-box;
	}

	.bw-editable-field {
		background-color: fade(@accentAssistTeal, 6%);
		border: 1px solid fade(@accentAssistTeal, 30%);
	}

	.bw-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 12px;
	}

	.bw-header-right {
		display: flex;
		gap: 8px;
		align-items: center;

		.button {
			margin: 0;
		}
	}

	.bw-meta {
		margin: 0;
		font-size: 13px;
		color: @textBody;
	}

	.bw-feedback {
		display: flex;
		flex-direction: column;
		gap: 0;
		background-color: var(--bw-accent-panel, fade(@accentAssistTeal, 6%));
		border: none;
		border-radius: @radiusSm;
	}

	.bw-feedback-toggle {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		width: 100%;
		background: transparent;
		border: none;
		padding: 10px 12px;
		cursor: pointer;
		color: @darkContrast;
		text-align: left;
	}

	.bw-feedback-title {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: var(--bw-accent-muted, fade(@accentAssistTeal, 70%));
		span {
			color: inherit;
		}
	}

	.bw-feedback-alert {
		font-size: 12px;
		font-weight: 800;
		color: @red;
	}

	.bw-feedback-toggle-icon {
		font-size: 12px;
		font-weight: 700;
		color: var(--bw-accent-muted-2, fade(@accentAssistTeal, 62%));
	}

	.bw-feedback-body {
		padding: 0 12px 10px 12px;
	}

	.bw-feedback-text {
		margin: 0;
		font-size: 13px;
		color: @darkContrast;
		white-space: pre-wrap;
	}

	.bw-quick-edits {
		display: flex;
		flex-wrap: wrap;
		gap: 5px;

		.button {
			margin: 0;
			border-color: var(--bw-accent-border, fade(@accentAssistTeal, 30%));
			color: var(--bw-accent, @accentAssistTeal);
			background-color: #fff;
		}
	}

	.bw-actions {
		display: flex;
		flex-direction: column;
		gap: 10px;

		.button {
			margin: 0;
			align-self: flex-start;
		}
	}

	.bw-help {
		font-size: 13px;
		color: @textBody;
		margin: 0;

		&--small {
			font-size: 12px;
			margin-top: 20px;
		}

		&.has-icon {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		&--interactive {
			cursor: pointer;
			text-decoration: underline;
		}

		&--interactive:focus-visible {
			outline: 2px solid fade(@accentAssistTeal, 45%);
			outline-offset: 4px;
			border-radius: @radiusSm;
		}
		&-text {
			color: @textBody;
		}
		&--enhance {
			font-weight: @bold;
			margin-bottom: 10px !important;
			color: @accentAssistTeal;
		}
	}

	.bw-error {
		margin: 20px 0 0;
		font-size: 13px;
		color: @red;
		background: fade(@red, 10%);
		border: 1px solid fade(@red, 25%);
		padding: 10px 12px;
		border-radius: @radiusSm;
	}

	.bw-feedback-section {
		margin-top: 20px;
		padding-top: 20px;
		border-top: 1px solid var(--bw-accent-panel-border, fade(@accentAssistTeal, 18%));
	}

	.bw-feedback-textarea {
		font-size: 13px;
		min-height: 70px;
	}

	.bw-feedback-actions {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 10px;

		.button {
			margin: 0;
		}
	}

	.bw-feedback-status {
		font-size: 13px;
		color: @accentAssistTeal;
		font-weight: @bold;
	}
}
@media (max-width: 767px) {
	.brief-writer {
		.bw-quick-edits {
			.button {
				padding: 10px;
			}
		}
	}
}
</style>
